/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  PayloadAction
} from '@reduxjs/toolkit';
import {
  CreateWorkerMutation,
  CreateWorkerMutationVariables,
  GetWorkerQuery,
  GetWorkerQueryVariables,
  Worker
} from '@/generated/graphql';
import { RootState, AsyncThunkConfig } from '@/stores/AppStore';
import { FetchResult } from '@apollo/client';
import AppConstants from '@/utils/AppConstants';

export const createWorker = createAsyncThunk<
  FetchResult<CreateWorkerMutation>,
  CreateWorkerMutationVariables,
  AsyncThunkConfig
>('worker/create', async (variables, thunkAPI) =>
  thunkAPI.extra.appSyncClient.query({
    query: AppConstants.APIS.WORKERS.CREATE(),
    variables
  })
);

export const getWorker = createAsyncThunk<
  FetchResult<GetWorkerQuery>,
  GetWorkerQueryVariables,
  AsyncThunkConfig
>(
  'worker/fetch',
  async ({ id }, thunkAPI) =>
    thunkAPI.extra.appSyncClient.query({
      query: AppConstants.APIS.GET_ENTITY.worker(),
      variables: {
        id
      }
    }),
  {
    serializeError: (x: any) => x
  }
);

const WorkerAdapter = createEntityAdapter<Worker>();

export const workerSlice = createSlice({
  name: 'worker',
  initialState: WorkerAdapter.getInitialState({
    loading: false,
    error: undefined
  }),
  reducers: {
    updateWorker: WorkerAdapter.updateOne,
    removeWorker: WorkerAdapter.removeOne,
    addWorker: (state, action: PayloadAction<Worker>) => {
      const worker = action.payload;
      const workerId = worker?.id;
      if (worker) {
        state.entities[workerId] = worker;
        if (state.ids.indexOf(worker.id) === -1) {
          state.ids.push(workerId);
        }
      }
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(createWorker.fulfilled, (state, action) => {
      const worker = action?.payload?.data?.createWorker as Worker;
      if (worker) {
        WorkerAdapter.addOne(state, worker);
      }
    });
    builder.addCase(getWorker.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error as any;
    });
    builder.addCase(getWorker.fulfilled, (state, { payload, meta }) => {
      const workerId = meta.arg.id;
      const worker = payload?.data?.getWorker!;

      if (worker) {
        // @ts-ignore
        state.entities[workerId] = {
          ...worker
        };

        if (state.ids.indexOf(worker.id) === -1) {
          state.ids.push(workerId);
        }
      }

      if (payload.errors) {
        state.error = payload?.errors as any;
      }

      state.loading = false;
    });
  }
});

export const workerQuerySelector = (state: RootState) => ({
  loading: state.worker.loading,
  error: state.worker.error
});

export default workerSlice.reducer;

export const { removeWorker, updateWorker, addWorker } = workerSlice.actions;

export const workerSelector = WorkerAdapter.getSelectors(
  (state: RootState) => state.worker
);
